import { dispatch, actions, stateRef, getFrameworkVar } from './states'
import { dedupPromise, isNonemptyArray, jwtDecode } from './utils'
import { tsgUserManagers, clearCurrentTsgId, getTsgAccess as getTsgAccessFn } from './tsg'
import { // only pick some from utils, not expose everything
  doLogin,
  doLogout,
  getAuthHeaders,
  getCSRFToken,
  getMd5Salt,
  getParam,
  getSession,
  getRedirectURL,
  getAdminClusterURL,
  getFawkesTenantId,
  checkIsFeatureEnabled,
  isValidRoute,
} from '../containers/main/ui-lib/utils' // TODO: '../containers/mainOneappNew/fawkesUtils'
import { fetchIamCustomRoleDefinitionAndCache } from './actions'
import { indexCustomRoles } from './access'
import cache from './cache'
import { _performance, __isDebugOn } from '../init/debug'

import type { NavItem } from './microAppTypes'
import type { FeatureInfo, IamCustomRolesDefinition, ReadonlyAccess } from './stateTypes'

export type { History } from 'history'
export type { Pendo } from './pendo'
export type { StoreState, AuthState, MainState, GlobalStore, GlobalStore as Store, ParsedPrn, Authed, AuthInfo, FeatureInfo, FawkesTenantInfo, UserState, ConfigLocation, LoggedInUser, AppRegion, AppManifests, AppManifest, IamPermission, IamPermissions, IamRoleDefinition, StringArraySet, Vars, ReadonlyAccess, IndexedArray, MarketplaceContent, DeveloperDefinedField, SupportAccount } from './stateTypes'
export type { Instance, Tsg, ReadonlyTsgTree, TsgTree, ReadonlyTsgInstances } from './indexedArrays'
export type { AsyncCache, SyncCache, CacheOptions } from './cache'
export type { NavItem, NavAction, NavParams, MicroAppConfig, MicroApp, MicroAppExport, SparkySettings, SparkyAvailFunc, AvailFunc, AvailParam, LazyFactory } from './microAppTypes'
export type { TsgLoadFnParam, TsgLoadFn } from './newMicroAppTypes'
export type { Entitlements, Credentials } from './TokenManagerTypes'
export type { TokenManager } from '@sparky/token-manager'

export const fawkesUtils = {
  doLogin,
  doLogout,
  getAuthHeaders,
  getCSRFToken,
  getMd5Salt,
  jwtDecode,
  getParam,
  getSession,
  getRedirectURL,
  getAdminClusterURL,
  getFawkesTenantId,
  checkIsFeatureEnabled,
  isValidRoute,
} as {
  doLogin: () => void,
  doLogout: () => void,
  getAuthHeaders: () => Record<string, string>,
  getCSRFToken: () => string,
  getMd5Salt: () => string,
  jwtDecode: typeof jwtDecode,
  getParam: (arg: string) => string | undefined,
  getSession: () => string | undefined,
  getRedirectURL: () => string,
  getAdminClusterURL: () => string,
  getFawkesTenantId: () => string | undefined,
  checkIsFeatureEnabled: (list?: FeatureInfo[], key?: string) => boolean,
  isValidRoute: (nav: NavItem) => boolean,
}

/**
 * @deprecated use fawkesUtils instead
 */
export { fawkesUtils as utils }

export const _resetAuth = (): void => {
  clearCurrentTsgId()
  actions.logout()
  setTimeout(() => {
    localStorage.clear()
    sessionStorage.clear()
  }, 100)
}

export const _getPerformanceTiming = () => _performance.timing
export const _setPreviewOverrides = (microAppIds: string | string[], overrides: any[], tsgId?: string | boolean): void => {
  if (!getFrameworkVar('overridable')) {
    console.warn('the current sparky shell is not overridable')
    return
  }
  const microApps = Array.isArray(microAppIds) ? microAppIds.join(',') : microAppIds || ''
  const overrideJson = isNonemptyArray(overrides) ? JSON.stringify(overrides) : ''
  if (!microApps && !overrideJson) {
    console.warn('please provide a list of micro-app ids and/or overrides')
    return
  }
  sessionStorage.setItem('#sparky-micro-apps', microApps)
  sessionStorage.setItem('#sparky-overrides', overrideJson)
  console.info('set preview overrides into session storage\nmicro-app ids: ', microApps, '\noverrides:', overrideJson ? overrides : '(none)')
  const url = new URL(window.location.href)
  if (tsgId) {
    url.searchParams.set('tsg_id', tsgId === true ? stateRef.current.auth?.tsg_id || '' : tsgId)
  }
  const params = new URLSearchParams()
  if (microApps) {
    params.set('sparky-micro-apps', microApps)
  }
  if (overrideJson) {
    params.set('sparky-overrides', overrideJson)
  }
  url.hash = `#${params}`
  console.info('the preview overrides will be forgotten after close the browser tab, but can be revisit using the url:', url.toString())
}

export const getTsgAccess = (tsg_id: string): Promise<ReadonlyAccess | undefined> => {
  const fn = async () => {
    let definitions = stateRef.current.auth?.rolePermissionDefinition
    if (definitions && definitions.customRoles?.tsg_id !== tsg_id) {
      const cacheKey = `custom-roles-${tsg_id}`
      const cached = await cache.get<IamCustomRolesDefinition>(cacheKey)
      if (cached) {
        Object.assign(indexCustomRoles(cached), { tsg_id })
        definitions = { ...definitions, customRoles: cached }
      }
      else {
        const customRoles = await fetchIamCustomRoleDefinitionAndCache(tsg_id)
        definitions = { ...definitions, customRoles }
      }
    }
    const access = await getTsgAccessFn(tsg_id, definitions)
    return access
  }
  return dedupPromise(`tsg_access:${tsg_id}`, fn)
}

Object.assign(dispatch, actions)

Object.defineProperties(tsgUserManagers, {
  getCurrentTsgId: {
    value: (): string | undefined => {
      return stateRef.current.auth?.tsg_id || tsgUserManagers.__current_tsg_id__
    }
  }
})

export const _getTsgUserManagers = () => {
  if (__isDebugOn()) {
    return tsgUserManagers
  }
  console.error('this method is not allowed')
}

/**
 * @deprecated micro-app should handle its own translation
 */
export const _T = (str: string): string => str

export { __isDebugOn } from '../init/debug'
export { SPARKY_FRAMEWORK } from './constants'
export * as hooks from './hooks'
export * as layout from '../containers/layout'
export { store, dispatch, getState, getAuthState, getMainState, events, actions, history, onAuthed } from './states'
export { inited, authed, loaded, authenticated, getInitSettings, getFrameworkVar, getFrameworkVars, getFrameworkServiceUrl, getCurrentPlatformRegion, isOidcClient, isAppCanBeLaunched } from './states'
export { prn, createUrnUtil } from './access'
export { hasMicroApp, hasMicroAppExported, hasExposedNamespace, getExposedNamespace, getMicroAppVars, importExposed, lazyImportComponent, getExposedSync } from './microApps'
export { hasTsgSupport, getTsgToken, getCurrentTsgId, getCurrentTsgToken, revokeTsgToken, getTsgIdFromUrl, cleanupTsgIdFromUrl } from './tsg'
export { indexTsgTree, indexTsgInstances, indexAppManifests, indexSupportAccounts } from './indexedArrays'
export { getDatadogRum, getPendo } from './externals'
export { GlobalStateContext, useStoreSelector } from '../containers/common/storeHook'
export { default as i18n, getGridLocale, getUserLanguage } from '../i18n'
export { default as StartupManager } from '../containers/main/StartupManager'
export { importLibs, moduleImport } from '../libs/base'
export { getAuthData, getToken, getTokenManager } from '../containers/main/TkmProvider'
export { default as AuthInfoSpinner } from '../containers/main/AuthInfoSpinner'
export { default as LoadingPlaceholder } from '../containers/common/LoadingPlaceholder'
export { loadJs, loadCss, clearLoadingMask, getRootElement, dedupPromise, jwtDecode } from './utils'
export * from './typeUtils'
